import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ref, set } from 'firebase/database';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGift,
    faUsers,
    faSpinner,
    faTimes,
    faDollarSign,
    faUserTie,
    faCalendarAlt,
    faSave,
    faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { generateId } from '../../utils/helpers';
import { database } from '../../firebase/firebase';
import { ShareRatios, SponsorExpense } from '../../types';
import { useTranslation } from 'react-i18next';
import HelpTip from '../common/HelpTip';
import AmountInput from '../common/AmountInput';
import ParticipantPicker from '../common/ParticipantPicker';
import { useCompactMode } from '../../hooks/useCompactMode';

interface SponsorFormProps {
    eventId: string;
    participants: { [userId: string]: string };
    /** The event's share ratios — sponsorships are shared out by them too. */
    shareRatios?: ShareRatios;
    currentUser: string;
    onSponsorAdded?: () => void;
    // Edit mode props — mirrors ExpenseForm, which handles create and edit in one place
    editSponsor?: SponsorExpense;
    onSave?: (sponsor: SponsorExpense) => Promise<void>;
    onCancel?: () => void;
    loading?: boolean;
    /** Fields only — see ExpenseForm. */
    bare?: boolean;
}

const todayISO = () => new Date().toISOString().split('T')[0];

const SponsorForm: React.FC<SponsorFormProps> = ({
                                                     eventId,
                                                     participants,
                                                     shareRatios,
                                                     currentUser,
                                                     onSponsorAdded,
                                                     editSponsor,
                                                     onSave,
                                                     onCancel,
                                                     loading = false,
                                                     bare = false
                                                 }) => {
    const { t } = useTranslation();
    const { compactMode } = useCompactMode();
    const isEditMode = !!editSponsor;

    const participantIds = useMemo(() => Object.keys(participants), [participants]);

    const [sponsorName, setSponsorName] = useState<string>(editSponsor?.sponsorName ?? '');
    const [amount, setAmount] = useState<string>(() => {
        if (!editSponsor) return '';
        const absAmount = Math.abs(editSponsor.amount);
        return compactMode ? (absAmount / 1000).toString() : absAmount.toString();
    });
    const [sponsorDate, setSponsorDate] = useState<string>(() => (
        editSponsor
            ? new Date(editSponsor.timestamp).toISOString().split('T')[0]
            : todayISO()
    ));
    const [beneficiaries, setBeneficiaries] = useState<string[]>(editSponsor?.splitBetween ?? []);
    // Sponsor type: 'external' (from outside) or 'participant' (from group member)
    const [sponsorType, setSponsorType] = useState<'external' | 'participant'>(
        editSponsor?.sponsorPayer ? 'participant' : 'external'
    );
    // For external sponsor: optionally someone holds the money
    const [hasHolder, setHasHolder] = useState<boolean>(!!editSponsor?.sponsorHolder);
    const [sponsorHolder, setSponsorHolder] = useState<string>(editSponsor?.sponsorHolder || '');
    // For participant sponsor
    const [sponsorPayer, setSponsorPayer] = useState<string>(editSponsor?.sponsorPayer || '');
    // Participant sponsor mode: 'payShare' or 'coverFirst'
    const [participantSponsorMode, setParticipantSponsorMode] = useState<'payShare' | 'coverFirst'>(
        editSponsor?.participantSponsorMode || 'payShare'
    );
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [formError, setFormError] = useState<string>('');

    const isFormDisabled = isSubmitting || loading;

    // Amount typed in K mode means thousands, so the real value is ×1.000
    const enteredAmountVnd = (parseFloat(amount) || 0) * (compactMode ? 1000 : 1);

    // Switching the đ/K unit must not change how much money the typed number means
    const prevCompactMode = React.useRef(compactMode);
    React.useEffect(() => {
        if (prevCompactMode.current === compactMode) return;
        prevCompactMode.current = compactMode;
        setAmount(prev => {
            const parsed = parseFloat(prev);
            if (isNaN(parsed)) return prev;
            return (compactMode ? parsed / 1000 : parsed * 1000).toString();
        });
    }, [compactMode]);

    // A new sponsorship covers everyone until the picker says otherwise
    React.useEffect(() => {
        if (!isEditMode) {
            setBeneficiaries(participantIds);
        }
    }, [participantIds, isEditMode]);

    const resetForm = () => {
        setSponsorName('');
        setAmount('');
        setBeneficiaries(participantIds);
        setSponsorType('external');
        setHasHolder(false);
        setSponsorHolder('');
        setSponsorPayer('');
        setParticipantSponsorMode('payShare');
        setSponsorDate(todayISO());
        setFormError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        const errorKey = isEditMode ? 'event.components.editSponsorForm.errors' : 'components.sponsorForm.errors';

        if (!sponsorName.trim() || !amount || beneficiaries.length === 0) {
            setFormError(t(`${errorKey}.fillAllFields`));
            return;
        }

        if (sponsorType === 'external' && hasHolder && !sponsorHolder) {
            setFormError(t(`${errorKey}.selectHolder`));
            return;
        }

        if (sponsorType === 'participant' && !sponsorPayer) {
            setFormError(t(`${errorKey}.selectSponsorPayer`));
            return;
        }

        const sponsorAmount = enteredAmountVnd;
        if (isNaN(sponsorAmount) || sponsorAmount <= 0) {
            setFormError(t('components.expenseForm.errors.invalidAmount'));
            return;
        }

        setIsSubmitting(true);

        try {
            // Keep the original time-of-day so same-day ordering is preserved
            const selectedDate = new Date(sponsorDate);
            const reference = isEditMode ? new Date(editSponsor.timestamp) : new Date();
            selectedDate.setHours(
                reference.getHours(), reference.getMinutes(),
                reference.getSeconds(), reference.getMilliseconds()
            );
            const timestamp = selectedDate.getTime();

            // Sponsor-mode fields are mutually exclusive; only the selected set is written
            const modeFields = sponsorType === 'external' && hasHolder && sponsorHolder
                ? { sponsorHolder, sponsorHolderName: participants[sponsorHolder] }
                : sponsorType === 'participant' && sponsorPayer
                    ? { sponsorPayer, sponsorPayerName: participants[sponsorPayer], participantSponsorMode }
                    : {};

            if (isEditMode && onSave) {
                const updatedSponsor = {
                    ...editSponsor,
                    sponsorName: sponsorName.trim(),
                    amount: -sponsorAmount, // sponsors stay negative
                    description: `Sponsorship from ${sponsorName.trim()}`,
                    splitBetween: beneficiaries,
                    timestamp,
                    ...modeFields,
                } as SponsorExpense;

                // Drop any stale fields from the previously selected sponsor mode
                (['sponsorHolder', 'sponsorHolderName', 'sponsorPayer', 'sponsorPayerName', 'participantSponsorMode'] as const)
                    .filter(key => !(key in modeFields))
                    .forEach(key => { delete (updatedSponsor as unknown as Record<string, unknown>)[key]; });

                await onSave(updatedSponsor);
                return;
            }

            const sponsorId = `sp_${generateId()}`;
            const sponsorExpense = {
                id: sponsorId,
                type: 'sponsor' as const,
                payer: 'sponsor',
                amount: -sponsorAmount,
                description: `Sponsorship from ${sponsorName.trim()}`,
                sponsorName: sponsorName.trim(),
                splitBetween: beneficiaries,
                timestamp,
                createdBy: currentUser,
                editableUntil: Date.now() + (5 * 60 * 1000),
                ...modeFields,
            };

            await set(ref(database, `eventExpenses/${eventId}/${sponsorId}`), sponsorExpense);

            resetForm();
            onSponsorAdded && onSponsorAdded();
        } catch (error) {
            console.error(isEditMode ? 'Error updating sponsorship:' : 'Error adding sponsor:', error);
            setFormError(isEditMode
                ? t('event.components.editSponsorForm.errors.updateFailed')
                : t('components.sponsorForm.errors.addFailed')
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClass = 'w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none';

    const isSubmitDisabled = isFormDisabled
        || beneficiaries.length === 0
        || (sponsorType === 'external' && hasHolder && !sponsorHolder)
        || (sponsorType === 'participant' && !sponsorPayer);

    const formFields = (
        <>
            {formError && (
                <div
                    className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center"
                    role="alert"
                    aria-live="polite"
                >
                    <FontAwesomeIcon icon={faExclamationCircle} className="text-red-600 dark:text-red-400 mr-2" aria-hidden="true" />
                    <span className="text-sm text-red-700 dark:text-red-300">{formError}</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Sponsor name */}
                <div>
                    <label htmlFor="sponsor-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('components.sponsorForm.sponsorName')} <span className="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="sponsor-name"
                        type="text"
                        value={sponsorName}
                        onChange={(e) => { setSponsorName(e.target.value); setFormError(''); }}
                        className={inputClass}
                        placeholder={t('components.sponsorForm.sponsorNamePlaceholder')}
                        required
                        disabled={isFormDisabled}
                    />
                </div>

                {/* Amount */}
                <div>
                    <label htmlFor="sponsor-amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <FontAwesomeIcon icon={faDollarSign} className="mr-2" aria-hidden="true" />
                        {t('components.sponsorForm.amount')} <span className="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <AmountInput
                        id="sponsor-amount"
                        value={amount}
                        onChange={(next) => { setAmount(next); setFormError(''); }}
                        placeholder={t('components.sponsorForm.amountPlaceholder')}
                        placeholderThousand={t('components.sponsorForm.amountPlaceholderThousand')}
                        className={inputClass}
                        required
                        disabled={isFormDisabled}
                    />
                </div>

                {/* Date */}
                <div>
                    <label htmlFor="sponsor-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" aria-hidden="true" />
                        {t('components.sponsorForm.date')}
                    </label>
                    <input
                        id="sponsor-date"
                        type="date"
                        value={sponsorDate}
                        onChange={(e) => { setSponsorDate(e.target.value); setFormError(''); }}
                        max={todayISO()}
                        className={inputClass}
                        disabled={isFormDisabled}
                    />
                </div>

                {/* Sponsor type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <FontAwesomeIcon icon={faUserTie} className="mr-2" aria-hidden="true" />
                        {t('components.sponsorForm.sponsorType')}
                        <HelpTip text={t('components.sponsorForm.help.sponsorType')} />
                    </label>
                    <div className="space-y-2">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                name="sponsorType"
                                value="external"
                                checked={sponsorType === 'external'}
                                onChange={() => { setSponsorType('external'); setFormError(''); }}
                                className="mr-2"
                                disabled={isFormDisabled}
                            />
                            <span className="text-sm text-gray-900 dark:text-white">
                                {t('components.sponsorForm.externalSponsor')}
                            </span>
                        </label>
                        <label className="flex items-center">
                            <input
                                type="radio"
                                name="sponsorType"
                                value="participant"
                                checked={sponsorType === 'participant'}
                                onChange={() => { setSponsorType('participant'); setFormError(''); }}
                                className="mr-2"
                                disabled={isFormDisabled}
                            />
                            <span className="text-sm text-gray-900 dark:text-white">
                                {t('components.sponsorForm.participantSponsor')}
                            </span>
                        </label>
                    </div>

                    {/* External sponsor options */}
                    {sponsorType === 'external' && (
                        <div className="mt-3 space-y-3">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={hasHolder}
                                    onChange={(e) => {
                                        setHasHolder(e.target.checked);
                                        if (!e.target.checked) setSponsorHolder('');
                                        setFormError('');
                                    }}
                                    className="mr-2"
                                    disabled={isFormDisabled}
                                />
                                <span className="text-sm text-gray-900 dark:text-white">
                                    {t('components.sponsorForm.hasHolder')}
                                </span>
                                <HelpTip text={t('components.sponsorForm.help.holder')} />
                            </label>
                            {hasHolder && (
                                <div>
                                    <label htmlFor="sponsor-holder" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t('components.sponsorForm.whoHoldsMoney')} <span className="text-red-500" aria-hidden="true">*</span>
                                    </label>
                                    <select
                                        id="sponsor-holder"
                                        value={sponsorHolder}
                                        onChange={(e) => { setSponsorHolder(e.target.value); setFormError(''); }}
                                        className={inputClass}
                                        required={hasHolder}
                                        disabled={isFormDisabled}
                                    >
                                        <option value="">{t('components.sponsorForm.selectHolder')}</option>
                                        {participantIds.map(id => (
                                            <option key={id} value={id}>{participants[id]}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Participant sponsor options */}
                    {sponsorType === 'participant' && (
                        <div className="mt-3 space-y-3">
                            <div>
                                <label htmlFor="sponsor-payer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('components.sponsorForm.whoIsSponsor')} <span className="text-red-500" aria-hidden="true">*</span>
                                </label>
                                <select
                                    id="sponsor-payer"
                                    value={sponsorPayer}
                                    onChange={(e) => { setSponsorPayer(e.target.value); setFormError(''); }}
                                    className={inputClass}
                                    required
                                    disabled={isFormDisabled}
                                >
                                    <option value="">{t('components.sponsorForm.selectSponsorPayer')}</option>
                                    {participantIds.map(id => (
                                        <option key={id} value={id}>{participants[id]}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('components.sponsorForm.participantSponsorModeLabel')}
                                    <HelpTip text={t('components.sponsorForm.help.mode')} />
                                </label>
                                <div className="space-y-2">
                                    {(['payShare', 'coverFirst'] as const).map(mode => (
                                        <label key={mode} className="flex items-start">
                                            <input
                                                type="radio"
                                                name="participantSponsorMode"
                                                value={mode}
                                                checked={participantSponsorMode === mode}
                                                onChange={() => setParticipantSponsorMode(mode)}
                                                className="mr-2 mt-1"
                                                disabled={isFormDisabled}
                                            />
                                            <div>
                                                <span className="text-sm text-gray-900 dark:text-white">
                                                    {t(`components.sponsorForm.${mode}Mode`)}
                                                </span>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {t(`components.sponsorForm.${mode}ModeHint`)}
                                                </p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Beneficiaries */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <FontAwesomeIcon icon={faUsers} className="mr-2" aria-hidden="true" />
                        {t('components.sponsorForm.beneficiaries')}
                        <HelpTip text={t('components.sponsorForm.help.beneficiaries')} />
                    </label>

                    <ParticipantPicker
                        participants={participants}
                        selected={beneficiaries}
                        onChange={(next) => { setBeneficiaries(next); setFormError(''); }}
                        currentUser={currentUser}
                        amount={enteredAmountVnd}
                        shareRatios={shareRatios}
                        tone="green"
                        disabled={isFormDisabled}
                        ariaLabel={t('components.sponsorForm.beneficiaries')}
                    />
                </div>

                {/* Buttons */}
                {isEditMode ? (
                    <div className="flex space-x-3 pt-4 form-sticky-actions">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="glass-btn-ghost flex-1 !py-2.5"
                            disabled={isFormDisabled}
                        >
                            {t('event.components.editSponsorForm.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitDisabled}
                            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center"
                        >
                            {isFormDisabled ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" aria-hidden="true" />
                                    {t('event.components.editSponsorForm.saving')}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faSave} className="mr-2" aria-hidden="true" />
                                    {t('event.components.editSponsorForm.saveChanges')}
                                </>
                            )}
                        </button>
                    </div>
                ) : (
                    <div className={`flex pt-4 ${bare ? 'form-sticky-actions' : ''}`}>
                        <button
                            type="submit"
                            disabled={isSubmitDisabled}
                            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" aria-hidden="true" />
                                    {t('components.sponsorForm.adding')}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faGift} className="mr-2" aria-hidden="true" />
                                    {t('components.sponsorForm.addSponsorship')}
                                </>
                            )}
                        </button>
                    </div>
                )}
            </form>
        </>
    );

    // ── Edit mode: render as modal ──
    // Via portal to body, for the same reason as ExpenseForm's edit modal: in
    // place, the page's header and bottom action bar would paint over it.
    if (isEditMode) {
        return createPortal(
            <div className="glass-modal-overlay is-fullscreen-mobile">
                <div className="glass-modal glass-modal-form max-w-lg w-full">
                    <div className="glass-modal-header">
                        <div className="flex items-center min-w-0">
                            <FontAwesomeIcon icon={faGift} className="text-green-600 dark:text-green-400 mr-2 flex-shrink-0" aria-hidden="true" />
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                                {t('event.components.editSponsorForm.title')}
                            </h2>
                        </div>
                        <button
                            onClick={onCancel}
                            className="glass-header-back !p-2 flex-shrink-0"
                            aria-label={t('event.expenseSection.close')}
                            disabled={isFormDisabled}
                        >
                            <FontAwesomeIcon icon={faTimes} className="text-sm" />
                        </button>
                    </div>
                    <div className="glass-modal-body has-sticky-actions">
                        {formFields}
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    // ── Create mode inside a sheet: the sheet header already names the form ──
    if (bare) {
        return formFields;
    }

    // ── Create mode: render as inline card ──
    return (
        <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <FontAwesomeIcon icon={faGift} className="mr-2 text-green-600 dark:text-green-400" aria-hidden="true" />
                    {t('components.sponsorForm.title')}
                </h3>
                {onCancel && (
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                        title={t('components.sponsorForm.backToAddExpense')}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}
            </div>
            {formFields}
        </div>
    );
};

export default SponsorForm;
